<template>
  {{ t("pages.agents.dialogs.capabilities.header") }}

  <div class="text-center">
    <div v-if="!currentAgent" class="mt-4 text-sm text-gray-500">
      {{ t("pages.agents.dialogs.capabilities.noAgentSelected") }}
    </div>

    <template v-else>
      <span v-for="capability in capabilities" :key="capability.key" class="mt-4 flex text-left text-sm">
        <span class="py-px pr-2">
          <ShieldCheckIcon v-if="capability.isAvailable" class="h-4 w-4" />
          <XMarkIcon v-else class="h-4 w-4" />
        </span>
        <a
          :aria-label="`${capability.docsLabel} (${t('common.opensInNewTab')})`"
          :href="capability.docsUrl"
          target="_blank"
        >
          {{ capability.title }}
        </a>
        <span v-if="capability.details" class="pl-2">
          {{ capability.details }}
        </span>
      </span>
    </template>
  </div>
</template>

<script setup>
import { ShieldCheckIcon, XMarkIcon } from "@heroicons/vue/20/solid";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
  currentAgent: {
    type: Object,
    default: null,
  },
});

const capabilities = computed(() => {
  if (!props.currentAgent?.Capabilities) {
    return [];
  }

  const caps = props.currentAgent.Capabilities;

  return [
    {
      key: "ipset",
      title: "IPSet:",
      isAvailable: !!caps.ipset?.command_available,
      docsUrl: "https://docs.knocknoc.io/books/admin-guide/page/ipset-linux-netfilteriptables",
      docsLabel: t("pages.agents.dialogs.capabilities.ipsetDocs"),
      details: caps.ipset?.ipsets
        ? caps.ipset.ipsets.map((ipset) => ipset.name).join(", ")
        : t("pages.agents.dialogs.capabilities.noIpsetsFound"),
    },
    {
      key: "pf",
      title: "Packet Filter (PF):",
      isAvailable: !!caps.pf?.command_available,
      docsUrl: "https://docs.knocknoc.io/books/admin-guide/page/ipset-linux-netfilteriptables", // TODO(shh) - Fix this
      docsLabel: t("pages.agents.dialogs.capabilities.pfDocs"),
      details: caps.pf?.tables
        ? caps.pf.tables.map((pf) => pf.name).join(", ")
        : t("pages.agents.dialogs.capabilities.noPfTablesFound"),
    },
    {
      key: "script",
      title: caps.script ? "Custom Script" : "Custom Scripts disabled",
      isAvailable: !!caps.script,
      docsUrl: "https://docs.knocknoc.io/books/admin-guide/page/custom-script",
      docsLabel: t("pages.agents.dialogs.capabilities.customScriptDocs"),
      details: null,
    },
    {
      key: "haproxy",
      title: "HAProxy",
      isAvailable: !!caps.haproxy,
      docsUrl: "https://docs.knocknoc.io/books/admin-guide/page/haproxy",
      docsLabel: t("pages.agents.dialogs.capabilities.haproxyDocs"),
      details: null,
    },
    {
      key: "other",
      title: "Other Capabilities",
      isAvailable: false,
      docsUrl: "https://docs.knocknoc.io/books/admin-guide/chapter/knocs-backends",
      docsLabel: t("pages.agents.dialogs.capabilities.knocsBackendsDocs"),
      details: null,
    },
  ];
});
</script>
